import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { Content, Link } from '@backstage/core-components';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';

interface HealthNoDataStateProps {
  context: 'team' | 'repo' | 'user' | 'incidents';
  entityName: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: theme.spacing(4),
  },
  card: {
    maxWidth: 500,
    padding: theme.spacing(4),
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    textAlign: 'center',
    backgroundColor: '#fff',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
  },
  icon: {
    fontSize: 48,
    color: '#8E9299',
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1A1A1A',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: 14,
    color: '#8E9299',
    marginBottom: theme.spacing(3),
  },
  infoBox: {
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    borderRadius: 4,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    textAlign: 'left',
  },
  infoTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#2B6CB0',
    marginBottom: theme.spacing(1),
  },
  infoText: {
    fontSize: 12,
    color: '#2B6CB0',
    lineHeight: 1.6,
  },
  contactText: {
    fontSize: 12,
    color: '#4A5568',
    lineHeight: 1.6,
  },
  link: {
    color: '#3182CE',
    fontWeight: 500,
  },
}));

export const HealthNoDataState: React.FC<HealthNoDataStateProps> = ({
  context,
  entityName,
}) => {
  const classes = useStyles();

  const contextLabel = context === 'team' ? 'team' : context === 'repo' ? 'repository' : context === 'incidents' ? 'incidents' : 'user';
  const displayName = entityName.replace('acl-dev-team-', '');

  return (
    <Content className={classes.root}>
      <Box className={classes.container}>
        <Paper className={classes.card} elevation={0}>
          <Box className={classes.iconContainer}>
            <AssessmentOutlinedIcon className={classes.icon} />
          </Box>
          <Typography className={classes.title}>
            No Health Data Available
          </Typography>
          <Typography className={classes.subtitle}>
            Health metrics for {contextLabel} "{displayName}" are not yet available.
          </Typography>

          <Box className={classes.infoBox}>
            <Typography className={classes.infoTitle}>
              Coming Soon
            </Typography>
            <Typography className={classes.infoText}>
              Code coverage support for additional languages (TypeScript, Python, and more)
              is currently in development and will be available soon.
            </Typography>
          </Box>

          <Typography className={classes.contactText}>
            Have questions or need support? Reach out to us on Slack in the{' '}
            <Link
              to="https://getweave.slack.com/archives/C0102287N3G"
              className={classes.link}
            >
              #test-infrastructure
            </Link>{' '}
            channel.
          </Typography>
        </Paper>
      </Box>
    </Content>
  );
};

export default HealthNoDataState;
